import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  MicIcon, VideoIcon, KeyIcon,
  PlayCircleIcon, CreditCardIcon
} from 'lucide-react';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import BetaAccessModal from '../components/modals/BetaAccessModal';
import { useAuth } from '../contexts/AuthContext';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showBetaModal, setShowBetaModal] = useState(false);

  const handleGetStarted = () => {
    navigate('/subscription');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 to-primary-700">
      <Helmet>
        <title>VoiceVision – AI-Powered Voice + Vision for the Web</title>
        <meta name="description" content="Experience the future of interaction with voice transcription, GPT-4 vision, and natural speech synthesis. Join the beta and get upgraded for free." />
        <meta property="og:title" content="VoiceVision – AI Voice + Vision Assistant" />
        <meta property="og:description" content="Join the VoiceVision beta. Buy Starter, get Pro access at launch." />
        <meta property="og:type" content="website" />
      </Helmet>
      
      {/* Navigation */}
      <nav className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <MicIcon className="h-8 w-8 text-white" />
            <span className="ml-2 text-2xl font-bold text-white">VoiceVision</span>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/login')}
            className="text-white border-white hover:bg-white/20 transition-colors"
          >
            Sign In
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            AI-Powered Voice + Vision for the Web
          </h1>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Experience the future of web interaction with advanced voice transcription,
            GPT-4 Vision analysis, and natural speech synthesis.
          </p>
          <div className="max-w-md mx-auto mb-12">
            <Button
              variant="primary"
              size="lg"
              onClick={handleGetStarted}
              className="bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all"
              leftIcon={<PlayCircleIcon className="h-5 w-5" />}
            >
              Get Started Free
            </Button>
          </div>
        </motion.div>
      </section>

      <BetaAccessModal
        isOpen={showBetaModal}
        onClose={() => setShowBetaModal(false)}
      />

      {/* Features */}
      <section className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-8">
          <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-colors h-full">
            <div className="p-6 text-white">
              <MicIcon className="h-10 w-10 mb-4" />
              <h3 className="text-xl font-bold mb-2">Voice Transcription</h3>
              <p className="text-white/80">
                Crystal-clear voice transcription powered by OpenAI's Whisper model.
              </p>
            </div>
          </Card>

          <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-colors h-full">
            <div className="p-6 text-white">
              <VideoIcon className="h-10 w-10 mb-4" />
              <h3 className="text-xl font-bold mb-2">GPT-4 Vision</h3>
              <p className="text-white/80">
                Advanced visual understanding and analysis using GPT-4's vision capabilities.
              </p>
            </div>
          </Card>

          <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-colors h-full">
            <div className="p-6 text-white">
              <KeyIcon className="h-10 w-10 mb-4" />
              <h3 className="text-xl font-bold mb-2">BYOK Support</h3>
              <p className="text-white/80">
                Bring Your Own Key (BYOK) - Use your personal OpenAI API key for unlimited sessions, 
                complete control over your usage, and no subscription limits.
              </p>
            </div>
          </Card>
        </div>
      </section>

      {/* Get Started Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/80">
            Join thousands of users already experiencing the future of AI-powered interaction.
            Choose from our subscription plans or bring your own OpenAI API key for unlimited access.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="primary"
            size="lg"
            onClick={handleGetStarted}
            className="bg-gradient-to-r from-secondary-500 to-primary-500 hover:from-secondary-600 hover:to-primary-600"
            leftIcon={<PlayCircleIcon className="h-5 w-5" />}
          >
            Start Free Trial
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate('/subscription')}
            className="border-white/30 text-white hover:bg-white/10"
            leftIcon={<CreditCardIcon className="h-5 w-5" />}
          >
            View Plans
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-8 mt-16 border-t border-white/10">
        <div className="flex flex-col md:flex-row justify-between items-center text-white/60">
          <div className="text-sm">
            © {new Date().getFullYear()} AI Chaperones. All rights reserved.
          </div>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <button onClick={() => navigate('/privacy')} className="hover:text-white">Privacy Policy</button>
            <button onClick={() => navigate('/terms')} className="hover:text-white">Terms of Service</button>
            <a href="mailto:support@aichaperones.com" className="hover:text-white">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;